import React, { useState, useEffect } from 'react';
import { useTheme } from './ThemeContext';
import CryptoJS from 'crypto-js';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { Helmet } from 'react-helmet'; // SEO meta tags

const TextTools = () => {
  const [activeTab, setActiveTab] = useState('case-converter');
  const darkMode = useTheme();

  // Case Converter
  const [caseText, setCaseText] = useState('');
  const [caseResult, setCaseResult] = useState('');

  // Character Counter
  const [countText, setCountText] = useState('');
  const [countStats, setCountStats] = useState({
    characters: 0,
    charactersNoSpaces: 0,
    words: 0,
    sentences: 0,
    paragraphs: 0
  });

  // Text Diff
  const [diffText1, setDiffText1] = useState('');
  const [diffText2, setDiffText2] = useState('');
  const [diffResult, setDiffResult] = useState('');

  // Text Cleaner
  const [cleanerText, setCleanerText] = useState('');
  const [cleanerResult, setCleanerResult] = useState('');

  // Lorem Ipsum
  const [loremParagraphs, setLoremParagraphs] = useState(3);
  const [loremResult, setLoremResult] = useState('');

  // Markdown Converter
  const [markdownText, setMarkdownText] = useState('');
  const [htmlResult, setHtmlResult] = useState('');


  const [htmlText, setHtmlText] = useState('');
  // Text Encryption
  const [encryptText, setEncryptText] = useState('');
  const [encryptKey, setEncryptKey] = useState('');
  const [encryptResult, setEncryptResult] = useState('');

  // Text Sorter
  const [sorterText, setSorterText] = useState('');
  const [sorterResult, setSorterResult] = useState('');

  // Find and Replace
  const [findReplaceText, setFindReplaceText] = useState('');
  const [findText, setFindText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [findReplaceResult, setFindReplaceResult] = useState('');

  // Text Beautifier
  const [beautifierText, setBeautifierText] = useState('');
  const [beautifierLanguage, setBeautifierLanguage] = useState('json');
  const [beautifierResult, setBeautifierResult] = useState('');

  // Toggle dark mode

  // Character counter functionality
  useEffect(() => {
    const stats = {
      characters: countText.length,
      charactersNoSpaces: countText.replace(/\s/g, '').length,
      words: countText.trim() === '' ? 0 : countText.trim().split(/\s+/).length,
      sentences: countText.trim() === '' ? 0 : countText.split(/[.!?]+/).filter(Boolean).length,
      paragraphs: countText.trim() === '' ? 0 : countText.split(/\n+/).filter(Boolean).length
    };
    setCountStats(stats);
  }, [countText]);

  // Case converter functionality
  const convertCase = (type) => {
    switch (type) {
      case 'upper':
        setCaseResult(caseText.toUpperCase());
        break;
      case 'lower':
        setCaseResult(caseText.toLowerCase());
        break;
      case 'title':
        setCaseResult(caseText.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()));
        break;
      case 'sentence':
        setCaseResult(caseText.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, c => c.toUpperCase()));
        break;
      default:
        setCaseResult(caseText);
    }
  };

  // Text cleaner functionality
  const cleanText = () => {
    let result = cleanerText;
    // Remove extra spaces
    result = result.replace(/\s+/g, ' ');
    // Remove extra line breaks
    result = result.replace(/\n+/g, '\n');
    // Trim
    result = result.trim();
    setCleanerResult(result);
  };

  // Lorem Ipsum generator
  const generateLorem = () => {
    const loremText = "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.";

    let result = '';
    for (let i = 0; i < loremParagraphs; i++) {
      result += loremText + '\n\n';
    }
    setLoremResult(result.trim());
  };

  // Simple markdown converter functionality
  const convertMarkdown = () => {
    let html = markdownText;
    // Headers
    html = html.replace(/^### (.*$)/gm, '<h3>$1</h3>');
    html = html.replace(/^## (.*$)/gm, '<h2>$1</h2>');
    html = html.replace(/^# (.*$)/gm, '<h1>$1</h1>');
    // Bold
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // Italic
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    // Links
    html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2">$1</a>');
    // Lists
    html = html.replace(/^\* (.*$)/gm, '<li>$1</li>');
    // Paragraphs
    html = html.replace(/^\s*$(?:\r\n?|\n)/gm, '</p><p>');
    html = '<p>' + html + '</p>';

    setHtmlResult(html);
  };

  // Simple encryption
  const encryptDecrypt = (action) => {
    if (!encryptKey) return;

    const result = [];
    for (let i = 0; i < encryptText.length; i++) {
      const charCode = encryptText.charCodeAt(i);
      const keyChar = encryptKey.charCodeAt(i % encryptKey.length);

      if (action === 'encrypt') {
        result.push(String.fromCharCode(charCode + keyChar % 26));
      } else {
        result.push(String.fromCharCode(charCode - keyChar % 26));
      }
    }

    setEncryptResult(result.join(''));
  };

  // Text sorter
  const sortText = (order) => {
    const lines = sorterText.split('\n').filter(Boolean);

    if (order === 'asc') {
      lines.sort();
    } else if (order === 'desc') {
      lines.sort().reverse();
    } else if (order === 'length') {
      lines.sort((a, b) => a.length - b.length);
    }

    setSorterResult(lines.join('\n'));
  };

  // Find and replace
  const findAndReplace = () => {
    if (!findText) return;

    const regex = new RegExp(findText, 'g');
    const result = findReplaceText.replace(regex, replaceText);
    setFindReplaceResult(result);
  };

  // Text beautifier
  const beautifyText = () => {
    try {
      let result = beautifierText;

      if (beautifierLanguage === 'json') {
        const parsed = JSON.parse(beautifierText);
        result = JSON.stringify(parsed, null, 2);
      } else if (beautifierLanguage === 'html') {
        // Simple HTML formatting
        result = beautifierText
          .replace(/></g, '>\n<')
          .replace(/(<\/[^>]+>)/g, '$1\n')
          .replace(/(<[^\/][^>]*[^\/]>)\s*/g, '$1\n');
      }

      setBeautifierResult(result);
    } catch (error) {
      setBeautifierResult('Error: Invalid format');
    }
  };

  // Text diff comparison
  const compareTexts = () => {
    // Simple diff implementation
    const result = [];
    const text1Lines = diffText1.split('\n');
    const text2Lines = diffText2.split('\n');

    for (let i = 0; i < Math.max(text1Lines.length, text2Lines.length); i++) {
      const line1 = text1Lines[i] || '';
      const line2 = text2Lines[i] || '';

      if (line1 === line2) {
        result.push(`  ${line1}`);
      } else {
        result.push(`- ${line1}`);
        result.push(`+ ${line2}`);
      }
    }

    setDiffResult(result.join('\n'));
  };


  // Convert Markdown to HTML
  const convertMarkdownToHTML = () => {
    const sanitizedHtml = DOMPurify.sanitize(marked.parse(markdownText));
    setHtmlText(sanitizedHtml);
  };

  // Convert HTML to Markdown (basic approach)
  const convertHTMLToMarkdown = () => {
    setMarkdownText(htmlText.replace(/<[^>]+>/g, ''));
  };

  // Encrypt Text
  const encrypt = () => {
    if (!encryptKey) return;
    const encrypted = CryptoJS.AES.encrypt(encryptText, encryptKey).toString();
    setEncryptResult(encrypted);
  };

  // Decrypt Text
  const decrypt = () => {
    if (!encryptKey) return;
    try {
      const bytes = CryptoJS.AES.decrypt(encryptText, encryptKey);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      setEncryptResult(decrypted || 'Invalid Key');
    } catch {
      setEncryptResult('Invalid Key');
    }
  };




  return (

    <>

      <Helmet>
        <title>Text Tools - Free Online Utilities | WebUtility.online</title>
        <meta
          name="description"
          content="Use our free online text tools at WebUtility.online — including case converters, whitespace removers, word counters, and more. Fast, secure, and easy to use."
        />
        <meta
          name="keywords"
          content="text tools, online text utilities, case converter, remove whitespace, word counter, text editor, WebUtility"
        />

        {/* Open Graph / Social Preview */}
        <meta property="og:title" content="Text Tools - Free Online Utilities | WebUtility.online" />
        <meta
          property="og:description"
          content="Quickly edit and format text online with WebUtility's free tools. No sign-up, no downloads."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://webutility.online/text-tools" />
        <meta property="og:image" content="https://webutility.online/assets/og-text-tools.jpg" />

        {/* Optional: Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Text Tools - Free Online Utilities" />
        <meta name="twitter:description" content="Clean and format text with WebUtility's free online tools." />
        <meta name="twitter:image" content="https://webutility.online/assets/og-text-tools.jpg" />
      </Helmet>

      <div className={` transition-colors bg-white/80`}>
        <div className={`container mx-auto px-4 py-8 bg-white/80'`}>


          {/* Navigation Tabs */}
          <div className={`flex flex-wrap gap-2 mb-6 border-b  bg-white/80'`}>
            <button
              className={`px-4 py-2 font-medium rounded-t-lg transition ${activeTab === 'case-converter' ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100'}`}
              onClick={() => setActiveTab('case-converter')}
            >
              Case Converter
            </button>
            <button
              className={`px-4 py-2 font-medium rounded-t-lg transition ${activeTab === 'char-counter' ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100'}`}
              onClick={() => setActiveTab('char-counter')}
            >
              Character Counter
            </button>
            <button
              className={`px-4 py-2 font-medium rounded-t-lg transition ${activeTab === 'text-diff' ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100'}`}
              onClick={() => setActiveTab('text-diff')}
            >
              Text Diff
            </button>
            <button
              className={`px-4 py-2 font-medium rounded-t-lg transition ${activeTab === 'text-cleaner' ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100'}`}
              onClick={() => setActiveTab('text-cleaner')}
            >
              Text Cleaner
            </button>
            <button
              className={`px-4 py-2 font-medium rounded-t-lg transition ${activeTab === 'lorem-ipsum' ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100'}`}
              onClick={() => setActiveTab('lorem-ipsum')}
            >
              Lorem Ipsum Generator
            </button>
            <button
              className={`px-4 py-2 font-medium rounded-t-lg transition ${activeTab === 'markdown' ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100'}`}
              onClick={() => setActiveTab('markdown')}
            >
              Convert Markdown
            </button>
            <button
              className={`px-4 py-2 font-medium rounded-t-lg transition ${activeTab === 'encryption' ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100'}`}
              onClick={() => setActiveTab('encryption')}
            >
              Encryption / Decryption
            </button>
            <button
              className={`px-4 py-2 font-medium rounded-t-lg transition ${activeTab === 'find-replace' ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100'}`}
              onClick={() => setActiveTab('find-replace')}
            >
              Find & Replace
            </button>
            <button
              className={`px-4 py-2 font-medium rounded-t-lg transition ${activeTab === 'beautifier' ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100'}`}
              onClick={() => setActiveTab('beautifier')}
            >
              Beautifier
            </button>
          </div>

          {/* Tool content */}
          <div className={` rounded-lg shadow-lg p-6 ${darkMode ? 'bg-white/80' : 'bg-gray-900/80'}`}>
            {/* Case Converter */}
            {activeTab === 'case-converter' && (
              <div>
                <h2 className="text-2xl font-bold mb-4">Case Converter</h2>
                <textarea
                  className="w-full p-4 border rounded-lg mb-4"
                  rows={6}
                  placeholder="Enter your text here..."
                  value={caseText}
                  onChange={(e) => setCaseText(e.target.value)}
                ></textarea>

                <div className="flex flex-wrap gap-2 mb-4">
                  <button
                    onClick={() => convertCase('upper')}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
                  >
                    UPPERCASE
                  </button>
                  <button
                    onClick={() => convertCase('lower')}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
                  >
                    lowercase
                  </button>
                  <button
                    onClick={() => convertCase('title')}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
                  >
                    Title Case
                  </button>
                  <button
                    onClick={() => convertCase('sentence')}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
                  >
                    Sentence case
                  </button>
                </div>

                {caseResult && (
                  <div className="mt-4">
                    <h3 className="text-lg font-semibold mb-2">Result:</h3>
                    <div className="p-4 bg-gray-100 rounded-lg">
                      {caseResult}
                    </div>
                    <button
                      onClick={() => navigator.clipboard.writeText(caseResult)}
                      className="mt-2 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition"
                    >
                      Copy to Clipboard
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Character Counter */}
            {activeTab === 'char-counter' && (
              <div>
                <h2 className="text-2xl font-bold mb-4">Character & Word Counter</h2>
                <textarea
                  className="w-full p-4 border rounded-lg mb-4"
                  rows={6}
                  placeholder="Enter your text here..."
                  value={countText}
                  onChange={(e) => setCountText(e.target.value)}
                ></textarea>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-100 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-indigo-600">{countStats.characters}</div>
                    <div className="text-sm text-gray-600">Characters</div>
                  </div>
                  <div className="bg-gray-100 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-indigo-600">{countStats.charactersNoSpaces}</div>
                    <div className="text-sm text-gray-600">Characters (no spaces)</div>
                  </div>
                  <div className="bg-gray-100 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-indigo-600">{countStats.words}</div>
                    <div className="text-sm text-gray-600">Words</div>
                  </div>
                  <div className="bg-gray-100 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-indigo-600">{countStats.sentences}</div>
                    <div className="text-sm text-gray-600">Sentences</div>
                  </div>
                  <div className="bg-gray-100 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-indigo-600">{countStats.paragraphs}</div>
                    <div className="text-sm text-gray-600">Paragraphs</div>
                  </div>
                </div>
              </div>
            )}

            {/* Text Diff */}
            {activeTab === 'text-diff' && (
              <div>
                <h2 className="text-2xl font-bold mb-4">Text Diff Checker</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block mb-2 font-medium">Original Text:</label>
                    <textarea
                      className="w-full p-4 border rounded-lg"
                      rows={6}
                      placeholder="Enter original text..."
                      value={diffText1}
                      onChange={(e) => setDiffText1(e.target.value)}
                    ></textarea>
                  </div>
                  <div>
                    <label className="block mb-2 font-medium">Changed Text:</label>
                    <textarea
                      className="w-full p-4 border rounded-lg"
                      rows={6}
                      placeholder="Enter changed text..."
                      value={diffText2}
                      onChange={(e) => setDiffText2(e.target.value)}
                    ></textarea>
                  </div>
                </div>

                <button
                  onClick={compareTexts}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
                >
                  Compare Texts
                </button>

                {diffResult && (
                  <div className="mt-4">
                    <h3 className="text-lg font-semibold mb-2">Diff Result:</h3>
                    <pre className="p-4 bg-gray-100 rounded-lg overflow-x-auto">
                      {diffResult}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* Text Cleaner */}
            {activeTab === 'text-cleaner' && (
              <div>
                <h2 className="text-2xl font-bold mb-4">Text Cleaner</h2>
                <textarea
                  className="w-full p-4 border rounded-lg mb-4"
                  rows={6}
                  placeholder="Enter text with extra spaces, line breaks, etc..."
                  value={cleanerText}
                  onChange={(e) => setCleanerText(e.target.value)}
                ></textarea>

                <button
                  onClick={cleanText}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
                >
                  Clean Text
                </button>

                {cleanerResult && (
                  <div className="mt-4">
                    <h3 className="text-lg font-semibold mb-2">Cleaned Text:</h3>
                    <div className="p-4 bg-gray-100 rounded-lg">
                      {cleanerResult}
                    </div>
                    <button
                      onClick={() => navigator.clipboard.writeText(cleanerResult)}
                      className="mt-2 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition"
                    >
                      Copy to Clipboard
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Lorem Ipsum */}
            {activeTab === 'lorem-ipsum' && (
              <div>
                <h2 className="text-2xl font-bold mb-4">Lorem Ipsum Generator</h2>

                <div className="mb-4">
                  <label className="block mb-2 font-medium">Number of paragraphs:</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    className="w-full p-2 border rounded"
                    value={loremParagraphs}
                    onChange={(e) => setLoremParagraphs(parseInt(e.target.value))}
                  />
                </div>

                <button
                  onClick={generateLorem}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
                >
                  Generate Lorem Ipsum
                </button>

                {loremResult && (
                  <div className="mt-4">
                    <h3 className="text-lg font-semibold mb-2">Generated Text:</h3>
                    <div className="p-4 bg-gray-100 rounded-lg whitespace-pre-wrap">
                      {loremResult}
                    </div>
                    <button
                      onClick={() => navigator.clipboard.writeText(loremResult)}
                      className="mt-2 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition"
                    >
                      Copy to Clipboard
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'markdown' && (<>
              <textarea value={markdownText} onChange={(e) => setMarkdownText(e.target.value)} className="w-full p-4 border rounded mb-4" placeholder="Enter Markdown..."></textarea>
              <button onClick={convertMarkdownToHTML} className="px-4 py-2 bg-blue-600 text-white rounded">Convert to HTML</button>
              <div className="mt-4 p-4 bg-gray-100 rounded">{htmlText}</div>
            </>)}
            {activeTab === 'encryption' && (<>
              <textarea value={encryptText} onChange={(e) => setEncryptText(e.target.value)} className="w-full p-4 border rounded mb-4" placeholder="Enter text to encrypt..."></textarea>
              <input type="text" value={encryptKey} onChange={(e) => setEncryptKey(e.target.value)} className="w-full p-2 border rounded mb-4" placeholder="Enter encryption key" />
              <div className="flex space-x-2">
                <button onClick={encrypt} className="px-4 py-2 bg-green-600 text-white rounded">Encrypt</button>
                <button onClick={decrypt} className="px-4 py-2 bg-red-600 text-white rounded">Decrypt</button>
              </div>
              {encryptResult && (
                <div className="mt-4 p-4 bg-gray-100 rounded break-words">{encryptResult}</div>
              )}
            </>)}
            {activeTab === 'find-replace' && (<>
              <textarea value={findReplaceText} onChange={(e) => setFindReplaceText(e.target.value)} className="w-full p-4 border rounded mb-4" placeholder="Enter text..."></textarea>
              <input type="text" value={findText} onChange={(e) => setFindText(e.target.value)} className="w-full p-2 border rounded mb-4" placeholder="Find..." />
              <input type="text" value={replaceText} onChange={(e) => setReplaceText(e.target.value)} className="w-full p-2 border rounded mb-4" placeholder="Replace with..." />
              <button onClick={findAndReplace} className="px-4 py-2 bg-purple-600 text-white rounded">Find & Replace</button>
              {findReplaceResult && (
                <div className="mt-4 p-4 bg-gray-100 rounded break-words">{findReplaceResult}</div>
              )}
            </>)}
            {activeTab === 'beautifier' && (<>
              <textarea value={beautifierText} onChange={(e) => setBeautifierText(e.target.value)} className="w-full p-4 border rounded mb-4" placeholder="Enter text to beautify..."></textarea>
              <select value={beautifierLanguage} onChange={(e) => setBeautifierLanguage(e.target.value)} className="w-full p-2 border rounded mb-4">
                <option value="json">JSON</option>
                <option value="html">HTML</option>
              </select>
              <button onClick={beautifyText} className="px-4 py-2 bg-green-600 text-white rounded">Beautify</button>
              {beautifierResult && (
                <div className="mt-4 p-4 bg-gray-100 rounded whitespace-pre-wrap">{beautifierResult}</div>
              )}
            </>)}
          </div>
          <section className={`p-8 rounded-xl shadow-lg mt-12 ${darkMode ? 'bg-white text-gray-700' : 'bg-white'}`}>
            <h2 className="text-2xl font-bold mb-4">📝 How to Use Text Tools Effectively</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Our collection of <strong>Text Tools</strong> is designed to save you time and enhance your productivity when working with any type of written content. Whether you’re a developer, writer, student, or data analyst, these tools can help you clean up, analyze, and optimize your text.
            </p>

            <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
              <li>
                <strong>Case Converter:</strong> Instantly switch between uppercase, lowercase, sentence case, and more. Great for correcting improperly formatted content.
              </li>
              <li>
                <strong>Character Counter:</strong> Count characters, words, and even spaces. Ideal for SEO meta tags, tweets, or form limits.
              </li>
              <li>
                <strong>Text Diff Checker:</strong> Compare two blocks of text and highlight the changes. Useful for content revisions or code snippets.
              </li>
              <li>
                <strong>Text Cleaner:</strong> Remove unwanted spaces, symbols, or formatting from copied content. Perfect for pasting from PDFs or messy sources.
              </li>
              <li>
                <strong>Find and Replace:</strong> Quickly replace words or phrases in bulk. Essential for editing large chunks of text.
              </li>
              <li>
                <strong>Lorem Ipsum Generator:</strong> Generate placeholder text for designs, layouts, and wireframes.
              </li>
            </ul>

            <p className="mt-4 text-gray-700 dark:text-gray-300">
              All tools run completely in your browser. No data is ever sent to our servers, keeping your content private and secure. Try any tool above to see how it can improve your workflow!
            </p>
          </section>

        </div>
      </div>


    </>


  );
};

export default TextTools;
